import {
    UseQueryResult,
    useMutation,
    useQuery,
    useQueryClient,
} from '@tanstack/react-query';
import { RQAPICall, RQAPICallWithData, HttpResponse } from 'store/APIClient';
import { dispatch } from 'store/store';
import {
    PatchRetrospectivePayload,
    RetrospectiveCreateFieldT,
    RetrospectiveT,
} from '../types';
import { TRACKABLE_EVENTS, trackEventPRH } from 'helpers/trackerHelpers';
const retrospectiveDataQueryKey = (retrospectiveId: number) =>
    `retrospective_data_${retrospectiveId}`;

/**
 * Hook to retrieve every retrospectives
 */
export const useGetAllRetrospective = (): UseQueryResult<RetrospectiveT[]> => {
    const endpoint = `human_resources/all_retrospectives`;

    return useQuery({
        queryKey: ['all_retrospectives'],
        queryFn: async () => {
            const getResponse = await RQAPICall({
                endpoint,
                method: 'GET',
            });
            return getResponse.content.data;
        },
        meta: {
            useGlobalOnError: true,
        },
    });
};

/**
 * Hook to get a retrospective from its id
 */
export const useGetRetrospective = (
    id: number,
): UseQueryResult<RetrospectiveT> => {
    const endpoint = `human_resources/retrospective/${id}`;

    return useQuery({
        queryKey: ['retrospective', id],

        queryFn: async () => {
            const getResponse = await RQAPICall({
                endpoint,
                method: 'GET',
            });
            return getResponse.content.data;
        },
        meta: {
            useGlobalOnError: true,
        },
    });
};

/**
 * Hook to create a retrospective
 */
export const useCreateRetrospective = () => {
    const endpoint = `human_resources/retrospective`;

    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({
            exercice,
            title,
            orgChartHrsSoftwareIds,
            etablissementSoftwareIds,
            isEtablissementsFilterActive,
            isPayBudgetFilterActive,
            payBudgetSoftwareIds,
        }: RetrospectiveCreateFieldT): Promise<
            HttpResponse<{ message: string; data: RetrospectiveT }>
        > =>
            RQAPICall({
                endpoint,
                method: 'POST',
                body: {
                    exercice,
                    title,
                    orgChartHrsSoftwareIds,
                    etablissementSoftwareIds,
                    isEtablissementsFilterActive,
                    isPayBudgetFilterActive,
                    payBudgetSoftwareIds,
                },
            }),

        onSuccess: (response) => {
            dispatch.vision.notifyUser({
                message: response.content.message,
                snackType: 'success',
            });
            queryClient.setQueryData(
                ['all_retrospectives'],
                (oldData: RetrospectiveT[]) => [
                    ...oldData,
                    response.content.data,
                ],
            );
            trackEventPRH(
                TRACKABLE_EVENTS.human_resources.retrospective.create,
                {
                    servicesNumber: (response.content.data as RetrospectiveT)
                        .orgChartHrs.length,
                    etablissementFilter: (
                        response.content.data as RetrospectiveT
                    ).isEtablissementsFilterActive,
                    retrospectiveId: (response.content.data as RetrospectiveT)
                        .id,
                    budgetFilter: (response.content.data as RetrospectiveT)
                        .isPayBudgetFilterActive,
                },
            );
        },
        meta: { useGlobalOnError: true },
    });
};

/**
 * Hook to update a retrospective
 */
export const usePatchRetrospective = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (Retrospective: PatchRetrospectivePayload) =>
            RQAPICallWithData<RetrospectiveT>({
                endpoint: `human_resources/retrospective/${Retrospective.id}`,
                method: 'PATCH',
                body: Retrospective,
            }),

        onSuccess: (response, variables) => {
            queryClient.setQueryData(
                ['retrospective', variables.id],
                () => response.content.data,
            );
            queryClient.invalidateQueries({
                queryKey: ['all_retrospectives'],
            });
        },
        meta: { useGlobalOnError: true },
    });
};

/**
 * Hook to delete a retrospective
 */
export const useDeleteRetrospective = (id: number) => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: () =>
            RQAPICall({
                endpoint: `human_resources/retrospective/${id}`,
                method: 'DELETE',
            }),

        onSuccess: (response) => {
            dispatch.vision.notifyUser({
                message: response.content.message,
                snackType: 'success',
            });
            queryClient.setQueryData(
                ['all_retrospectives'],
                (oldData: RetrospectiveT[]) =>
                    oldData.filter((Retrospective) => Retrospective.id !== id),
            );
            queryClient.removeQueries({
                queryKey: [retrospectiveDataQueryKey(id)],
            });
        },
        meta: { useGlobalOnError: true },
    });
};
